import React, { useMemo, useState } from 'react'
import { trans } from 'sharedKernel/i18n'
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'
import { Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { hasAccess } from 'sharedKernel/auth/behavior'
import { getHubRoute, removeTagsHTML } from 'sharedKernel/handlers'
import { ROUTES } from 'sharedKernel/constants'
import { Filter } from 'components/Filter'
import { NoContent } from 'components/NoContent'
import { Pagination } from 'rsuite'
import '../../../Ecosystem/styles.css'
import { formatOpportunityVerticalsDisplay, getOpportunityCoverImage } from 'domain/feature/hub/company/scouting/scoutingOpportunityDisplay'
import type { ScoutingOpportunity } from 'domain/feature/hub/company/scouting/api/scoutingService'
import {
  DEFAULT_OPPORTUNITY_IMAGE,
  OPPORTUNITY_LIST_PAGE_SIZE,
} from './constants'
import * as S from './style'

export interface Scouting {
  id: string
  title: string
  description: string
  requesterName?: string
  operationArea?: string
  verticals?: string[]
  otherVertical?: string
  status?: string
  statusId?: number
  createdAt: string
  updatedAt: string
  coverImagePath?: string
  files?: Array<{ path?: string; type?: string; name?: string; id?: string }>
  style?: {
    coverImagePath?: string
  }
  imagePath?: string
  image?: string
  totalStartupsInteressed?: number
  interestDemonstrationsCount?: number
  demonstratedInterestCount?: number
  interestCount?: number
  scouting?: Array<{
    id: string
    title: string
    stages: Array<{
      title: string
      sort: number
    }>
    criteria: Array<{
      scoutingCriterionId: number
      condition: string
      valueOne: string
      valueTwo?: string
    }>
  }>
}

export interface HubCompanyScoutingCarouselListProps {
  scoutingList: Scouting[]
  setIsDelete?: React.Dispatch<React.SetStateAction<boolean>>
  showNoContent?: boolean
  noContentText?: string
  noContentImage?: string
}

const displayOrDash = (value?: string | number | null): string => {
  if (value === null || value === undefined || value === '') return '-'
  return String(value)
}

const getCoverImage = (scouting: Scouting): string =>
  getOpportunityCoverImage(scouting as unknown as ScoutingOpportunity)

const getInterestDemonstrationsCount = (scouting: Scouting): number => {
  const value =
    scouting.totalStartupsInteressed ??
    scouting.interestDemonstrationsCount ??
    scouting.demonstratedInterestCount ??
    scouting.interestCount

  if (value === null || value === undefined) return 0
  return value
}

const getDescriptionPlainText = (description?: string): string => {
  if (!description?.trim()) return ''
  return removeTagsHTML(description).trim()
}

const getDescriptionPreview = (description?: string): string => {
  const plainText = getDescriptionPlainText(description)
  if (!plainText) return '-'
  return plainText
}

export const HubCompanyScoutingCarouselList = ({
  scoutingList,
  setIsDelete = () => { },
  showNoContent = false,
  noContentText,
  noContentImage,
}: HubCompanyScoutingCarouselListProps): JSX.Element => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  const labels = {
    viewOpportunity: trans('scouting.opportunity.list.viewOpportunity'),
    createOpportunities: trans('scouting.opportunity.list.createOpportunities'),
    interestDemonstrations: trans('scouting.opportunity.list.interestDemonstrations'),
    noSearchResults: trans('scouting.opportunity.list.noSearchResults'),
    manageTitle: trans('scouting.opportunity.list.manageTitle'),
    manageSubtitle: trans('scouting.opportunity.list.manageSubtitle'),
    emptyList: trans('scouting.opportunity.list.emptyList'),
    searchPlaceholder: trans('scouting.opportunity.list.searchPlaceholder'),
    itemsPerPage: trans('scouting.opportunity.list.itemsPerPage'),
  }

  const filteredList = useMemo(() => {
    const term = appliedSearchTerm.trim().toLowerCase()
    if (!term) return scoutingList

    return scoutingList.filter((scouting) => {
      const searchableFields = [
        scouting.title,
        removeTagsHTML(scouting.description ?? ''),
        scouting.operationArea,
        formatOpportunityVerticalsDisplay(scouting),
        scouting.requesterName,
      ]

      return searchableFields.some((field) =>
        String(field ?? '').toLowerCase().includes(term)
      )
    })
  }, [appliedSearchTerm, scoutingList])

  const totalPages = Math.max(1, Math.ceil(filteredList.length / OPPORTUNITY_LIST_PAGE_SIZE))
  const paginatedList = useMemo(() => {
    const startIndex = (currentPage - 1) * OPPORTUNITY_LIST_PAGE_SIZE
    return filteredList.slice(startIndex, startIndex + OPPORTUNITY_LIST_PAGE_SIZE)
  }, [currentPage, filteredList])

  const handleSearch = (term: string): void => {
    setAppliedSearchTerm(term)
    setCurrentPage(1)
  }

  const handlePageChange = (eventKey: string | number): void => {
    setCurrentPage(Number(eventKey))
  }

  const handlePrevPage = (): void => {
    setCurrentPage((prev) => Math.max(1, prev - 1))
  }

  const handleNextPage = (): void => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
  }

  const handleCardClick = (scoutingId: string): void => {
    navigate(`/hub/company/scouting/${scoutingId}`)
  }

  const handleViewClick = (event: React.MouseEvent, scoutingId: string): void => {
    event.stopPropagation()
    navigate(`/hub/company/scouting/${scoutingId}`)
  }

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>): void => {
    event.currentTarget.onerror = null
    event.currentTarget.src = DEFAULT_OPPORTUNITY_IMAGE
  }

  const opportunityCards = paginatedList.map((scouting) => {
    const interestCount = getInterestDemonstrationsCount(scouting)

    return (
      <S.OpportunityCard key={scouting.id} onClick={() => handleCardClick(scouting.id)}>
        <S.CardContent>
          <S.CardImageWrapper>
            <S.CardImage
              src={getCoverImage(scouting)}
              alt={displayOrDash(scouting.title)}
              onError={handleImageError}
            />
          </S.CardImageWrapper>

          <S.CardBody>
            <S.CardTextContent>
              <S.CardTitle>{displayOrDash(scouting.title)}</S.CardTitle>
              <S.CardDescription title={getDescriptionPlainText(scouting.description)}>
                {getDescriptionPreview(scouting.description)}
              </S.CardDescription>
            </S.CardTextContent>
            <S.CardFooter>
              <S.CardMeta>
                {labels.interestDemonstrations}{' '}
                {interestCount}
              </S.CardMeta>
              <S.ViewButton onClick={(event) => handleViewClick(event, scouting.id)}>
                {labels.viewOpportunity}
              </S.ViewButton>
            </S.CardFooter>
          </S.CardBody>
        </S.CardContent>
      </S.OpportunityCard>
    )
  })

  const showEmptyState = showNoContent && scoutingList.length === 0
  const showNoSearchResults = !showEmptyState && filteredList.length === 0

  return (
    <>
      <S.MobileContainerPadding />
      <S.PageHeader>
        <S.HeaderLeft>
          <S.PageTitle>{labels.manageTitle}</S.PageTitle>
          <S.PageSubtitle>{labels.manageSubtitle}</S.PageSubtitle>
        </S.HeaderLeft>
        {hasAccess('challenge', 'create') && (
          <Button
            variant="primary"
            className="fw-bold"
            onClick={() => {
              navigate(getHubRoute(ROUTES.HUB.COMPANY.NEW_SCOUTING))
            }}
          >
            <FontAwesomeIcon icon={['fas', 'plus']} className="me-2" />
            {labels.createOpportunities}
          </Button>
        )}
      </S.PageHeader>

      <S.SearchSection>
        <Filter
          currentSearchTerm={searchTerm}
          btnClick={handleSearch}
          onFilterChange={(term: string) => {
            setSearchTerm(term)
            if (term === '') {
              handleSearch('')
            }
          }}
          minSearchTermLength={1}
          searchPlaceholder={labels.searchPlaceholder}
        />
      </S.SearchSection>

      {showEmptyState ? (
        <NoContent
          text={noContentText || labels.emptyList}
          image={noContentImage}
          disableImageFilter={true}
        />
      ) : showNoSearchResults ? (
        <S.EmptySearchMessage>{labels.noSearchResults}</S.EmptySearchMessage>
      ) : (
        <>
          <S.OpportunitiesList>{opportunityCards}</S.OpportunitiesList>

          <S.PaginationFooter>
            <S.PaginationControls>
              <S.PaginationPrevButton
                type="button"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
              >
                <S.PaginationChevronIcon className="bi bi-chevron-left" />
              </S.PaginationPrevButton>
              <Pagination
                activePage={currentPage}
                pages={totalPages}
                maxButtons={3}
                ellipsis={true}
                boundaryLinks={true}
                onSelect={handlePageChange}
                size="sm"
                total={filteredList.length}
                className="custom-pagination"
              />
              <S.PaginationNextButton
                type="button"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                <S.PaginationChevronIcon className="bi bi-chevron-right" />
              </S.PaginationNextButton>
            </S.PaginationControls>
            <S.ItemsPerPageLabel className="items-per-page">
              {OPPORTUNITY_LIST_PAGE_SIZE} {labels.itemsPerPage}
            </S.ItemsPerPageLabel>
          </S.PaginationFooter>
        </>
      )}
    </>
  )
}
